import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout } from "../../components"

/* REST API */
import { detail } from "../../api/Transaction"

/* Helpers */
import { utils } from "../../helpers"

/* Translate */
import { t } from "../../translates/translate"

/* Icons */
import { CloseIcon, CopyIcon, HashIcon, ClockIcon, OperatorIcon, TableIcon, StudioIcon, PlayerIcon, VipIcon, BalanceIcon, RefundIcon, StatusIcon, WalletIcon } from "./TransactionIcons"

/* Utils */
import { getState, getStateText, getGame, getCurrency, isEmpty, formatDate, formatTime, formatAgo } from "./TransactionUtils"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* Widget Transaction Detail */
class Detail extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            status: "loading",
            id: null,
            transaction: null,
            round: [],
        }
    }

    /* Open Action */
    open = (id) => {
        this.setState({ visible: true, status: "loading", id, transaction: null, round: [] }, () => this.load(id))
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, id: null, transaction: null, round: [] })
    }

    /* Load Action */
    load = (id) => {

        /* Check Parameters */
        if (!id) {
            return
        }

        /* Send to REST API */
        detail(id).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", transaction: response.data.transaction, round: response.data.round })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission" })
            }
            else if (response.status === 498) {
                this.setState({ status: "key" })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => {
            this.setState({ status: "network" })
        })

    }

    /* Copy Action — the number of a transaction goes to the clipboard straight from the modal */
    copy = (value) => {

        /* Check Value */
        if (!value) {
            return
        }

        /* Check Clipboard — an insecure origin has none */
        if (!navigator.clipboard) {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(String(value)).then(() => {
            Swal.fire(utils.notification(t("gtx.copied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
        })

    }

    /* Draw State */
    _state = (value) => {

        /* Fields */
        const state = getState(value)

        /* Send Result */
        return <div className={`gtx--state ${state}`}>{getStateText(value)}</div>
    }

    /* Draw Amount — the money the operator moved */
    _amount = (transaction) => {

        /* Fields */
        const currency = getCurrency(transaction.currency)
        const type = String(transaction.type || "")

        return (
            <div className={`gtx--amount ${type}`}>

                <div className="gtx--amount-main">
                    <div className="gtx--amount-title">{t("gtx.amount")}</div>
                    <div className="gtx--amount-value">{utils.convertor(transaction.total, currency, 1)}</div>
                    <div className="gtx--amount-usd">{utils.convertor(transaction.totalUSD, "USD", 1)}</div>
                </div>

                <div className="gtx--amount-side">
                    <span className={`gtx--type ${type}`}>{type ? t(`gtx.${type}`) : "—"}</span>
                    {this._state(transaction.status)}
                    {parseInt(transaction.isRefund) === 1 && <span className="gtx--refund">{t("gtx.isRefund")}</span>}
                </div>

            </div>
        )
    }

    /* Draw One Information Item */
    _item = (icon, title, value, mono = false) => {

        return (
            <div className="gtx--info-item">

                {icon}

                <div className="gtx--info-text">
                    <div className="gtx--info-title">{title}</div>
                    <div className={`gtx--info-value ${mono ? "mono" : ""}`}>{isEmpty(value) ? "—" : value}</div>
                </div>

            </div>
        )
    }

    /* Draw Section */
    _section = (title, icon, children) => {

        return (
            <div className="gtx--section">

                <div className="gtx--section-head">
                    <div className="gtx--section-title">
                        {icon}
                        {title}
                    </div>
                </div>

                {children}

            </div>
        )
    }

    /* Draw Balance — empty means the operator sent no balance at all */
    _balance = (value, currency) => isEmpty(value) ? "—" : utils.convertor(value, currency, 1)

    /* Draw Result — the raw answer of the operator, a pending transaction has none yet */
    _result = (result) => {

        /* Check Result */
        if (!result) {
            return this._section(t("gtx.result"), <StatusIcon />, <div className="gtx--chips-empty">{t("gtx.noResult")}</div>)
        }

        /* Pretty Print JSON */
        let text = result

        try {
            text = JSON.stringify(JSON.parse(result), null, 4)
        }
        catch {
            text = result
        }

        /* Send Result */
        return this._section(t("gtx.result"), <StatusIcon />, <div className="gtx--code">{text}</div>)
    }

    /* Draw Round — the other transactions of the same round */
    _round = (round, currency) => {

        /* Check Round */
        if (!round || round.length === 0) {
            return null
        }

        return this._section(
            `${t("gtx.roundTransactions")} (${round.length})`,
            <HashIcon />,
            <div className="gtx--round">
                {round.map((item, index) =>
                    <div className={`gtx--round-row ${parseInt(item.id) === parseInt(this.state.id) ? "current" : ""}`} key={`${index}`}>

                        <div className="gtx--round-reason">
                            <div className="gtx--round-name">{utils.checkValue(item.reason, "—")}</div>
                            <div className="gtx--round-note">
                                {parseInt(item.box) > 0 ? <span className="gtx--box">BOX {item.box}</span> : null}
                                <span>{formatTime(item.createdAt)}</span>
                            </div>
                        </div>

                        <div className="gtx--round-side">
                            <span className={`gtx--type ${item.type}`}>{item.type ? t(`gtx.${item.type}`) : "—"}</span>
                            <div className="gtx--round-total">{utils.convertor(item.total, currency, 1)}</div>
                            {this._state(item.status)}
                        </div>

                    </div>
                )}
            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, id, transaction, round } = this.state

        /* Check Status */
        if (status !== "ok" || !transaction) {
            return (
                <div className="gtx--modal-loading">
                    <InnerLayout inner status={status} reload={() => this.load(id)} />
                </div>
            )
        }

        /* Fields */
        const currency = getCurrency(transaction.currency)

        return (
            <>
                {/* Amount */}
                {this._amount(transaction)}

                {/* Main */}
                {this._section(t("gtx.main"), <OperatorIcon />,
                    <div className="gtx--info">
                        {this._item(<OperatorIcon />, t("gtx.operator"), transaction.operator)}
                        {this._item(<TableIcon />, t("gtx.game"), getGame(transaction))}
                        {this._item(<StudioIcon />, t("gtx.studio"), transaction.studio)}
                        {this._item(<PlayerIcon />, t("gtx.playerName"), transaction.firstName)}
                        {this._item(<HashIcon />, t("gtx.playerID"), transaction.player, true)}
                        {this._item(<VipIcon />, t("gtx.playerType"), (
                            <span className="gtx--tags">
                                {parseInt(transaction.isVip) === 1 && <span className="gtx--tag vip">VIP</span>}
                                {parseInt(transaction.isTester) === 1 && <span className="gtx--tag tester">{t("gtx.tester")}</span>}
                                {parseInt(transaction.isVip) !== 1 && parseInt(transaction.isTester) !== 1 && <span className="gtx--tag">{t("gtx.regular")}</span>}
                            </span>
                        ))}
                    </div>
                )}

                {/* Transaction */}
                {this._section(t("gtx.transaction"), <HashIcon />,
                    <div className="gtx--info">
                        {this._item(<HashIcon />, t("gtx.number"), transaction.number, true)}
                        {this._item(<HashIcon />, t("gtx.round"), transaction.round, true)}
                        {this._item(<HashIcon />, t("gtx.session"), transaction.sid, true)}
                        {this._item(<StatusIcon />, t("gtx.reason"), transaction.reason)}
                        {this._item(<TableIcon />, t("gtx.box"), parseInt(transaction.box) > 0 ? transaction.box : "—")}
                        {this._item(<RefundIcon />, t("gtx.refund"), parseInt(transaction.isRefund) === 1 ? t("gtx.yes") : t("gtx.no"))}
                        {this._item(<PlayerIcon />, t("gtx.admin"), transaction.admin)}
                    </div>
                )}

                {/* Money */}
                {this._section(t("gtx.money"), <WalletIcon size={13} />,
                    <div className="gtx--info">
                        {this._item(<WalletIcon size={13} />, t("gtx.currency"), currency)}
                        {this._item(<WalletIcon size={13} />, t("gtx.rate"), transaction.exchanged)}
                        {this._item(<BalanceIcon />, t("gtx.balanceBefore"), this._balance(transaction.balanceBefore, currency))}
                        {this._item(<BalanceIcon />, t("gtx.balanceAfter"), this._balance(transaction.balanceAfter, currency))}
                    </div>
                )}

                {/* Dates */}
                {this._section(t("gtx.dates"), <ClockIcon />,
                    <div className="gtx--info">
                        {this._item(<ClockIcon />, t("gtx.createdAt"), formatDate(transaction.createdAt))}
                        {this._item(<ClockIcon />, t("gtx.updatedAt"), formatDate(transaction.updatedAt))}
                        {this._item(<ClockIcon />, t("gtx.ago"), formatAgo(transaction.createdAt))}
                    </div>
                )}

                {/* Result */}
                {this._result(transaction.result)}

                {/* Round */}
                {this._round(round, currency)}
            </>
        )
    }

    render = () => {

        /* Fields */
        const { visible, transaction } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="gtx--modal"
                overlayClassName="gtx--overlay"
                closeTimeoutMS={200}
            >

                <div className="gtx--modal-header">

                    <div className={`gtx--modal-icon ${transaction ? getState(transaction.status) : ""}`}>
                        <WalletIcon size={18} />
                    </div>

                    <div className="gtx--modal-head-text">
                        <h2>{transaction ? utils.checkValue(transaction.number, t("gtx.detailTitle")) : t("gtx.detailTitle")}</h2>
                        <p>{transaction ? `${utils.checkValue(transaction.operator, "—")} · ${getGame(transaction)}` : t("gtx.detailSubtitle")}</p>
                    </div>

                    {transaction &&
                        <div onClick={() => this.copy(transaction.number)} className="gtx--modal-action" title={t("gtx.copyNumber")}>
                            <CopyIcon size={15} />
                        </div>
                    }

                    <div onClick={() => this.close()} className="gtx--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                <div className="gtx--modal-body">
                    {this._content()}
                </div>

                <div className="gtx--modal-footer">
                    <div onClick={() => this.close()} className="gtx--button primary">{t("filter.cancel")}</div>
                </div>

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(Detail)
